import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import StopIcon from "@material-ui/icons/Stop";
import { Hidden } from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import RecordingTimer from "./RecordingTimer";
import LinkPreview, { getLinkPreviewData } from "../LinkPreview";
import {
  normalizeFilesForUpload,
  normalizeFileForUpload,
} from "../../utils/upload";

const AUDIO_CONSTRAINTS = {
  audio: {
    sampleRate: { ideal: 48000 },
    sampleSize: { ideal: 16 },
    channelCount: { ideal: 1 },
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

const RECORDING_AUDIO_BITS_PER_SECOND = 48000;
const PRESENCE_MIN_GAP_MS = 1500;
const PRESENCE_HEARTBEAT_BASE_MS = 6500;
const PRESENCE_HEARTBEAT_JITTER_MS = 1200;
const TYPING_IDLE_TIMEOUT_MS = 2200;

const getBestRecordingMimeType = () => {
  if (
    typeof MediaRecorder === "undefined" ||
    typeof MediaRecorder.isTypeSupported !== "function"
  ) {
    return "";
  }
  if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) {
    return "audio/ogg;codecs=opus";
  }
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
    return "audio/webm;codecs=opus";
  }
  return "";
};

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / 1024 ** unitIndex;
  return `${size < 10 && unitIndex > 0 ? size.toFixed(1) : Math.round(size)} ${units[unitIndex]}`;
};

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    background: "#eee",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },
  newMessageBox: {
    background: "#eee",
    width: "100%",
    minWidth: 0,
    gap: theme.spacing(0.25),
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },
  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: "#fff",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    minWidth: 0,
    position: "relative",
  },
  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },
  sendMessageIcons: {
    color: "grey",
  },
  uploadInput: {
    display: "none",
  },
  viewMediaInputWrapper: {
    display: "flex",
    width: "100%",
    boxSizing: "border-box",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
    backgroundColor: "#f7f8fa",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },
  selectedMediaList: {
    display: "flex",
    width: "100%",
    gap: theme.spacing(1),
    overflowX: "auto",
    paddingBottom: theme.spacing(0.5),
  },
  selectedMediaItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: "0 0 250px",
    minWidth: 0,
    padding: theme.spacing(1),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("xs")]: { flexBasis: 220 },
  },
  selectedMediaPreview: {
    width: 56,
    height: 56,
    flex: "none",
    objectFit: "cover",
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
  },
  selectedMediaDetails: { flex: 1, minWidth: 0 },
  selectedMediaName: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  selectedMediaActions: {
    display: "flex",
    width: "100%",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    zIndex: 100,
  },
  circleLoading: {
    color: "#ffcc00",
  },
  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },
  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },
  replyginMsgBody: {
    padding: 10,
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },
  replyginContactMsgSideColor: {
    width: "4px",
    backgroundColor: "#35cd96",
  },
  replyginSelfMsgSideColor: {
    width: "4px",
    backgroundColor: "#6bcbef",
  },
  messageContactName: {
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: "#ffffff",
    border: "1px solid #CCC",
    left: 0,
    width: "100%",
    zIndex: 10,
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        "&:hover": { background: "#F1F1F1", cursor: "pointer" },
      },
    },
  },
  composerPreviewWrapper: {
    width: "100%",
    padding: "8px 14px 0",
    boxSizing: "border-box",
  },
}));

const MessageInput = ({
  ticketStatus,
  ticket,
  onAddPendingMessage,
  onRemovePendingMessages,
}) => {
  const classes = useStyles();
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const mediaPreviewUrlsRef = useRef(new Set());
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const inputRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const isCancellingRecordingRef = useRef(false);
  const presenceLastSentRef = useRef({ typing: 0, recording: 0 });
  const presenceStateRef = useRef(null);
  const presenceTimerRef = useRef(null);
  const typingIdleTimerRef = useRef(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const linkPreview = getLinkPreviewData(inputMessage);

  const buildPendingMessage = ({
    body,
    mediaType = null,
    mediaUrl = null,
    fileName = null,
  }) => ({
    id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    body,
    mediaType,
    mediaUrl,
    fromMe: true,
    read: 1,
    ack: 0,
    user,
    queue: ticket?.queue,
    userId: user?.id,
    queueId: ticket?.queueId,
    isPending: true,
    createdAt: new Date().toISOString(),
    quotedMsg: replyingMessage,
    isDeleted: false,
    contact: null,
    fileName,
  });

  const getMediaTypeFromFile = (file) => {
    const fileType = file?.type || "";
    if (fileType.startsWith("image/")) return "image";
    if (fileType.startsWith("video/")) return "video";
    if (fileType.startsWith("audio/")) return "audio";
    return "document";
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "" || loading) return;
    stopPresence();

    const trimmedMessage = inputMessage.trim();
    const message = {
      read: 1,
      fromMe: true,
      body: trimmedMessage,
      quotedMsg: replyingMessage,
    };
    const pendingMessage = buildPendingMessage({ body: message.body });

    onAddPendingMessage?.(pendingMessage);
    setLoading(true);
    setInputMessage("");
    setShowEmoji(false);
    setReplyingMessage(null);

    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      onRemovePendingMessages?.([pendingMessage.id]);
      setInputMessage(trimmedMessage);
      setReplyingMessage(replyingMessage);
      toastError(err);
    } finally {
      setLoading(false);
      inputRef.current.focus();
    }
  };

  const clearTypingIdleTimer = () => {
    if (typingIdleTimerRef.current) {
      clearTimeout(typingIdleTimerRef.current);
      typingIdleTimerRef.current = null;
    }
  };

  const resetTypingIdleTimer = () => {
    clearTypingIdleTimer();
    typingIdleTimerRef.current = setTimeout(() => {
      stopPresence();
    }, TYPING_IDLE_TIMEOUT_MS);
  };

  const handleChangeInput = (e) => {
    const nextValue = e.target.value;
    setInputMessage(nextValue);
    handleLoadQuickAnswer(nextValue);

    if (isRecording || ticketStatus !== "open") return;

    if (nextValue.trim()) {
      startPresence("typing");
      resetTypingIdleTimer();
      return;
    }

    clearTypingIdleTimer();
    stopPresence();
  };

  const handleLoadQuickAnswer = async (value) => {
    if (value?.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: value.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        setTypeBar(data.quickAnswers?.length > 0);
      } catch {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleQuickAnswersClick = (value) => {
    setInputMessage(value);
    setTypeBar(false);
    if (ticketStatus === "open" && !isRecording && value?.trim()) {
      startPresence("typing");
      resetTypingIdleTimer();
    }
  };

  const handleAddEmoji = (e) => {
    setInputMessage((prev) => prev + e.native);
    if (ticketStatus === "open" && !isRecording) {
      startPresence("typing");
      resetTypingIdleTimer();
    }
  };

  const handleChangeMedias = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selectedFiles.length) return;
    normalizeFilesForUpload(selectedFiles)
      .then((files) => {
        const attachments = files.map((file) => {
          const previewUrl = URL.createObjectURL(file);
          mediaPreviewUrlsRef.current.add(previewUrl);
          return { file, previewUrl };
        });
        setMedias((current) => [...current, ...attachments]);
      })
      .catch((error) => toast.error(error.message));
  };

  const releaseMediaPreview = (previewUrl) => {
    if (!previewUrl || !mediaPreviewUrlsRef.current.has(previewUrl)) return;
    URL.revokeObjectURL(previewUrl);
    mediaPreviewUrlsRef.current.delete(previewUrl);
  };

  const handleRemoveMedia = (previewUrl) => {
    releaseMediaPreview(previewUrl);
    setMedias((current) => current.filter((media) => media.previewUrl !== previewUrl));
  };

  const handleClearMedias = () => {
    medias.forEach((media) => releaseMediaPreview(media.previewUrl));
    setMedias([]);
  };

  const handleUploadMedia = async () => {
    stopPresence();
    const pendingMessages = medias.map(({ file }) =>
      buildPendingMessage({ body: file.name, mediaType: getMediaTypeFromFile(file), mediaUrl: URL.createObjectURL(file), fileName: file.name })
    );

    pendingMessages.forEach((message) => onAddPendingMessage?.(message));
    setLoading(true);
    const formData = new FormData();
    formData.append("fromMe", "true");

    medias.forEach(({ file }) => {
      formData.append("medias", file);
      formData.append("body", file.name);
    });

    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      onRemovePendingMessages?.(pendingMessages.map((message) => message.id));
      toastError(err);
    } finally {
      pendingMessages.forEach((message) => {
        if (message.mediaUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(message.mediaUrl);
        }
      });
      setLoading(false);
      medias.forEach((media) => releaseMediaPreview(media.previewUrl));
      setMedias([]);
    }
  };

  const stopStreamTracks = () => {
    if (!streamRef.current) return;
    streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const uploadRecordedAudio = async (audioBlob) => {
    const extension = audioBlob.type.includes("ogg") ? "ogg" : "webm";
    const filename = `audio_${Date.now()}.${extension}`;
    const rawAudioFile = new File([audioBlob], filename, { type: audioBlob.type });
    const audioFile = await normalizeFileForUpload(rawAudioFile);
    const formData = new FormData();
    formData.append("fromMe", "true");
    formData.append("medias", audioFile);
    formData.append("body", filename);

    if (replyingMessage?.id) {
      formData.append("quotedMsgId", replyingMessage.id);
    }

    const pendingMessage = buildPendingMessage({
      body: filename,
      mediaType: "audio",
      mediaUrl: URL.createObjectURL(audioFile),
      fileName: filename,
    });

    onAddPendingMessage?.(pendingMessage);

    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      onRemovePendingMessages?.([pendingMessage.id]);
      throw err;
    } finally {
      if (pendingMessage.mediaUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(pendingMessage.mediaUrl);
      }
    }
  };

  const sendPresence = async (state, force = false) => {
    if (state !== "stop") {
      const now = Date.now();
      const lastStateSentAt = presenceLastSentRef.current[state] || 0;
      if (!force && now - lastStateSentAt < PRESENCE_MIN_GAP_MS) return;
      presenceLastSentRef.current[state] = now;
    }

    try {
      await api.post(`/messages-typing/${ticketId}`, { state });
    } catch (err) {
      // Presenca e opcional e nao deve bloquear o fluxo de mensagens.
    }
  };

  const clearPresenceTimer = () => {
    if (presenceTimerRef.current) {
      clearTimeout(presenceTimerRef.current);
      presenceTimerRef.current = null;
    }
  };

  const getPresenceHeartbeatMs = () => {
    const jitter = Math.floor(
      (Math.random() * 2 - 1) * PRESENCE_HEARTBEAT_JITTER_MS
    );
    return Math.max(3000, PRESENCE_HEARTBEAT_BASE_MS + jitter);
  };

  const schedulePresenceHeartbeat = (state) => {
    clearPresenceTimer();

    presenceTimerRef.current = setTimeout(() => {
      if (presenceStateRef.current !== state) return;
      sendPresence(state);
      schedulePresenceHeartbeat(state);
    }, getPresenceHeartbeatMs());
  };

  const startPresence = (state) => {
    if (ticketStatus !== "open") return;
    if (presenceStateRef.current !== state) {
      clearPresenceTimer();
      presenceStateRef.current = state;
    }
    sendPresence(state, true);
    schedulePresenceHeartbeat(state);
  };

  const stopPresence = () => {
    const previousState = presenceStateRef.current;
    presenceStateRef.current = null;
    clearPresenceTimer();
    clearTypingIdleTimer();
    if (previousState) {
      sendPresence("stop", true);
    }
  };

  const handleStartRecording = async () => {
    if (loading || ticketStatus !== "open") return;

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Gravação de áudio não suportada neste navegador.");
      return;
    }

    try {
      stopPresence();
      const stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
      streamRef.current = stream;

      const mimeType = getBestRecordingMimeType();
      const recorderOptions = {
        audioBitsPerSecond: RECORDING_AUDIO_BITS_PER_SECOND,
      };
      if (mimeType) {
        recorderOptions.mimeType = mimeType;
      }

      recorderRef.current = new MediaRecorder(stream, recorderOptions);
      recordingChunksRef.current = [];
      isCancellingRecordingRef.current = false;

      recorderRef.current.ondataavailable = (event) => {
        if (event.data?.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorderRef.current.onstop = async () => {
        setIsRecording(false);

        if (isCancellingRecordingRef.current) {
          recordingChunksRef.current = [];
          recorderRef.current = null;
          stopStreamTracks();
          return;
        }

        if (!recordingChunksRef.current.length) {
          stopStreamTracks();
          return;
        }

        const audioBlob = new Blob(recordingChunksRef.current, {
          type: mimeType || "audio/webm",
        });
        recordingChunksRef.current = [];
        setLoading(true);

        try {
          await uploadRecordedAudio(audioBlob);
          setReplyingMessage(null);
        } catch (err) {
          if (err?.message) {
            toast.error(err.message);
          } else {
            toastError(err);
          }
        } finally {
          setLoading(false);
          recorderRef.current = null;
          stopStreamTracks();
          stopPresence();
        }
      };

      recorderRef.current.start(250);
      setIsRecording(true);
      startPresence("recording");
      setInputMessage("");
      setTypeBar(false);
    } catch (err) {
      toastError(err);
      stopStreamTracks();
      setIsRecording(false);
      stopPresence();
    }
  };

  const handleStopRecording = () => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") return;
    stopPresence();
    recorderRef.current.stop();
  };

  const handleCancelRecording = () => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") return;
    stopPresence();
    const activeRecorder = recorderRef.current;
    isCancellingRecordingRef.current = true;
    recordingChunksRef.current = [];
    setIsRecording(false);
    activeRecorder.ondataavailable = null;
    activeRecorder.onstop = () => {
      recordingChunksRef.current = [];
      recorderRef.current = null;
      stopStreamTracks();
    };
    activeRecorder.stop();
  };

  useEffect(() => {
    return () => {
      stopPresence();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        isCancellingRecordingRef.current = true;
        recorderRef.current.onstop = null;
        recorderRef.current.stop();
      }
      stopStreamTracks();
      mediaPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      mediaPreviewUrlsRef.current.clear();
    };
  }, []);

  if (medias.length > 0) {
    const getPreview = ({ file, previewUrl }) => {
      const fileType = file.type || "";
      if (fileType.startsWith("image/")) {
        return (
          <img
            src={previewUrl}
            alt={file.name}
            className={classes.selectedMediaPreview}
          />
        );
      }
      if (fileType.startsWith("video/")) {
        return (
          <video
            src={previewUrl}
            className={classes.selectedMediaPreview}
            muted
            controls
          />
        );
      }
      if (fileType.startsWith("audio/")) {
        return (
          <audio
            src={previewUrl}
            controls
            style={{ width: 64 }}
            aria-label={file.name}
          />
        );
      }
      return <AttachFileIcon className={classes.sendMessageIcons} />;
    };
    return (
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <div className={classes.selectedMediaList}>
          {medias.map(({ file, previewUrl }) => (
            <div className={classes.selectedMediaItem} key={previewUrl}>
              {getPreview({ file, previewUrl })}
              <div className={classes.selectedMediaDetails}>
                <Typography className={classes.selectedMediaName} variant="body2" title={file.name}>{file.name}</Typography>
                <Typography variant="caption" color="textSecondary">{formatFileSize(file.size)}</Typography>
              </div>
              <IconButton size="small" onClick={() => handleRemoveMedia(previewUrl)} disabled={loading} aria-label={`Remover ${file.name}`}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </div>
        <div className={classes.selectedMediaActions}>
          {loading && <CircularProgress size={24} className={classes.circleLoading} />}
          <Button onClick={handleClearMedias} disabled={loading}>Cancelar</Button>
          <Button onClick={handleUploadMedia} disabled={loading} variant="contained" color="primary" startIcon={<SendIcon />}>
            Enviar {medias.length > 1 ? `(${medias.length})` : "arquivo"}
          </Button>
        </div>
      </Paper>
    );
  }

  return (
    <Paper square elevation={0} className={classes.mainWrapper}>
      {replyingMessage && (
        <div className={classes.replyginMsgWrapper}>
          <div className={classes.replyginMsgContainer}>
            <span
              className={clsx(classes.replyginContactMsgSideColor, {
                [classes.replyginSelfMsgSideColor]: !replyingMessage.fromMe,
              })}
            />
            <div className={classes.replyginMsgBody}>
              {!replyingMessage.fromMe && (
                <span className={classes.messageContactName}>
                  {replyingMessage.contact?.name}
                </span>
              )}
              {replyingMessage.body}
            </div>
          </div>
          <IconButton onClick={() => setReplyingMessage(null)}>
            <ClearIcon />
          </IconButton>
        </div>
      )}

      {ticketStatus === "open" && linkPreview && !isRecording && (
        <div className={classes.composerPreviewWrapper}>
          <LinkPreview
            url={linkPreview.url}
            title={linkPreview.title}
            subtitle={linkPreview.subtitle}
          />
        </div>
      )}

      <div className={classes.newMessageBox}>
        <Hidden only={["sm", "xs"]}>
          <IconButton
            disabled={loading || isRecording}
            onClick={() => setShowEmoji(!showEmoji)}
          >
            <MoodIcon className={classes.sendMessageIcons} />
          </IconButton>

          {showEmoji && (
            <div className={classes.emojiBox}>
              <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                <Picker
                  perLine={16}
                  showPreview={false}
                  onSelect={handleAddEmoji}
                />
              </ClickAwayListener>
            </div>
          )}
        </Hidden>

        <input
          multiple
          type="file"
          id="upload-button"
          className={classes.uploadInput}
          onChange={handleChangeMedias}
        />
        <label htmlFor="upload-button">
          <IconButton component="span" disabled={loading || isRecording} aria-label="Anexar arquivos">
            <AttachFileIcon className={classes.sendMessageIcons} />
          </IconButton>
        </label>

        <div className={classes.messageInputWrapper}>
          {isRecording ? (
            <>
              <RecordingTimer />
              <InputBase
                className={classes.messageInput}
                value="Gravando áudio..."
                disabled
              />
              <IconButton onClick={handleCancelRecording} disabled={loading}>
                <CancelIcon className={classes.sendMessageIcons} />
              </IconButton>
            </>
          ) : (
            <InputBase
              inputRef={inputRef}
              className={classes.messageInput}
              placeholder={
                ticketStatus === "open"
                  ? i18n.t("messagesInput.placeholderOpen")
                  : i18n.t("messagesInput.placeholderClosed")
              }
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={loading || ticketStatus !== "open"}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
          )}

          {typeBar && (
            <ul className={classes.messageQuickAnswersWrapper}>
              {quickAnswers.map((v, i) => (
                <li key={i}>
                  <a onClick={() => handleQuickAnswersClick(v.message)}>
                    {`${v.shortcut} - ${v.message}`}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        {inputMessage.trim() ? (
          <IconButton onClick={handleSendMessage} disabled={loading}>
            <SendIcon className={classes.sendMessageIcons} />
          </IconButton>
        ) : (
          <IconButton
            onClick={isRecording ? handleStopRecording : handleStartRecording}
            disabled={loading || ticketStatus !== "open"}
          >
            {isRecording ? (
              <StopIcon className={classes.sendMessageIcons} />
            ) : (
              <MicIcon className={classes.sendMessageIcons} />
            )}
          </IconButton>
        )}
      </div>
    </Paper>
  );
};

export default MessageInput;
